import { Directive, DirectiveBinding } from 'vue'
import './loading.css'
// 引入类型扩展
import '../types/loading.d.ts'

interface LoadingOptions {
  fullscreen?: boolean
  text?: string
  background?: string
}

const createLoadingElement = (options: LoadingOptions = {}): HTMLElement => {
  const loadingEl = document.createElement('div')
  loadingEl.className = options.fullscreen ? 'v-loading-fullscreen' : 'v-loading'

  // 创建加载动画元素
  const spinner = document.createElement('div')
  spinner.className = 'v-loading-spinner'

  // 创建加载圆环
  const circular = document.createElement('div')
  circular.className = 'v-loading-circular'

  // 创建文字提示
  if (options.text) {
    const text = document.createElement('div')
    text.className = 'v-loading-text'
    text.textContent = options.text
    loadingEl.appendChild(text)
  }

  // 设置背景色
  if (options.background) {
    loadingEl.style.backgroundColor = options.background
  }

  // 组合元素
  spinner.appendChild(circular)
  loadingEl.appendChild(spinner)

  return loadingEl
}

const vLoading: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    // 默认值处理 - 修复：布尔值应该只表示是否显示，而不是是否全屏
    const options: LoadingOptions = typeof binding.value === 'object' ? binding.value : { fullscreen: false }

    // 添加定位样式，确保loading元素正确定位
    if (!options.fullscreen) {
      const position = getComputedStyle(el).position
      if (position === 'static' || position === '') {
        el.style.position = 'relative'
      }

      // 确保元素有足够的高度显示loading
      if (el.offsetHeight < 100) {
        el.style.minHeight = '100px'
      }
    }

    if (binding.value) {
      // 移除可能已存在的loading元素
      if (el._loading_instance) {
        el.removeChild(el._loading_instance)
        delete el._loading_instance
      }

      if (el._loading_fullscreen_instance) {
        document.body.removeChild(el._loading_fullscreen_instance)
        document.body.style.overflow = ''
        delete el._loading_fullscreen_instance
      }

      const loadingEl = createLoadingElement(options)

      if (options.fullscreen) {
        document.body.appendChild(loadingEl)
        document.body.style.overflow = 'hidden'
        el._loading_fullscreen_instance = loadingEl
      } else {
        el.appendChild(loadingEl)
        el._loading_instance = loadingEl
      }
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    // 值没有变化时不处理
    if (binding.value === binding.oldValue) return

    // 修复：布尔值应该只表示是否显示，而不是是否全屏
    const options: LoadingOptions = typeof binding.value === 'object' ? binding.value : { fullscreen: false }

    // 显示loading
    if (binding.value) {
      // 移除可能已存在的loading元素
      if (el._loading_instance) {
        el.removeChild(el._loading_instance)
        delete el._loading_instance
      }

      if (el._loading_fullscreen_instance) {
        document.body.removeChild(el._loading_fullscreen_instance)
        document.body.style.overflow = ''
        delete el._loading_fullscreen_instance
      }

      const loadingEl = createLoadingElement(options)

      if (options.fullscreen) {
        document.body.appendChild(loadingEl)
        document.body.style.overflow = 'hidden'
        el._loading_fullscreen_instance = loadingEl
      } else {
        // 确保元素有足够的高度显示loading
        const position = getComputedStyle(el).position
        if (position === 'static' || position === '') {
          el.style.position = 'relative'
        }

        if (el.offsetHeight < 100) {
          el.style.minHeight = '100px'
        }

        el.appendChild(loadingEl)
        el._loading_instance = loadingEl
      }
    }
    // 移除loading
    else {
      if (el._loading_fullscreen_instance) {
        document.body.removeChild(el._loading_fullscreen_instance)
        document.body.style.overflow = ''
        delete el._loading_fullscreen_instance
      }

      if (el._loading_instance) {
        el.removeChild(el._loading_instance)
        delete el._loading_instance
      }
    }
  },

  unmounted(el: HTMLElement) {
    // 清理全屏loading
    if (el._loading_fullscreen_instance) {
      document.body.removeChild(el._loading_fullscreen_instance)
      document.body.style.overflow = ''
    }

    // 清理元素loading
    if (el._loading_instance) {
      el.removeChild(el._loading_instance)
    }
  },
}

export default vLoading
